import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabase";

type Mode = "password" | "magic";

function safeInternalPath(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/admin";
  return raw;
}

export default function AdminLogin() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeInternalPath(searchParams.get("next"));
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Already logged in? Redirect (honour ?next= for deep links e.g. avatar creator).
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) nav(nextPath, { replace: true });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (session) nav(nextPath, { replace: true });
    });
    return () => sub.subscription.unsubscribe();
  }, [nav, nextPath]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setLoading(true);

    try {
      if (mode === "password") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        // onAuthStateChange will redirect
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}${nextPath}` },
        });
        if (error) throw error;
        setInfo(`Magic link envoyé à ${email}. Ouvre ton mail.`);
      }
    } catch (e: any) {
      setErr(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) {
      setErr("Entre ton email d'abord");
      return;
    }
    setErr(null);
    setInfo(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${nextPath}`,
    });
    if (error) setErr(error.message);
    else setInfo(`Email de reset envoyé à ${email}.`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ivory">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 p-8 bg-white rounded-xl shadow"
      >
        <h1 className="text-2xl font-semibold">VideoLink admin</h1>

        <div className="flex bg-ivory rounded-lg p-1">
          {(["password", "magic"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 py-1.5 text-sm rounded-md transition ${
                mode === m ? "bg-white shadow-sm" : "text-concrete"
              }`}
            >
              {m === "password" ? "Password" : "Magic link"}
            </button>
          ))}
        </div>

        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          autoComplete="email"
          className="w-full px-4 py-3 border border-stone rounded-lg focus:outline-none focus:border-charcoal"
        />

        {mode === "password" && (
          <>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-stone rounded-lg focus:outline-none focus:border-charcoal"
            />
            <button
              type="button"
              onClick={forgotPassword}
              className="text-xs text-concrete hover:text-charcoal underline"
            >
              Forgot password?
            </button>
          </>
        )}

        {err && <div className="text-sm text-red-600">{err}</div>}
        {info && <div className="text-sm text-green-700">{info}</div>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-charcoal text-ivory rounded-lg font-medium disabled:opacity-50"
        >
          {loading
            ? "…"
            : mode === "password"
            ? "Sign in"
            : "Send magic link"}
        </button>
      </form>
    </div>
  );
}
